import { useEffect, useState } from 'react';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import { Checkbox, InputSeparator, InputSuffix, TextBox } from '@progress/kendo-react-inputs';
import { showMessage } from '../languages/_showmessages';
import { Button } from '@progress/kendo-react-buttons';
import { docAIApi } from '../apis/docai';
import { DocAICitationsModel, DocAIModel, DocAIResultModel } from '../models/docaiModel';
import ReactMarkdown from 'react-markdown';
import { useFolderContext } from '../context/FolderContext';
import { usePageContext } from '../context/PageContext';
import { usePageLoadContext } from '../context/PageLoadContext';
import { findFolderAndPageByPageId, findFolderById } from '../services/shared';
import { useTokenContext } from '../context/TokenContext';
import { useFoldersContext } from '../context/FoldersContext';
import { useDocAIContext } from '../context/DocAIContext';
import { format } from 'date-fns';
import TypewriterEffect from './TypewriterEffect';
import { isMobile } from 'react-device-detect';

interface DocAIControlsProps {
  libraries: string[];
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const DocAI: React.FC<DocAIControlsProps> = ({ libraries, setIsVisible }) => { 
  const [ prompt, setPrompt ] = useState('');
  const [ reasoningtext, setReasoningText ] = useState(String.fromCodePoint(0x0001F847) + ' Raciocínio');
  const [ refresh, setRefresh ] = useState(true);
  const [ items, setItems ] = useState<DocAIResultModel[]>([]);
  const { tokenContext } = useTokenContext();
  const { Folders } = useFoldersContext();
  const { setFolder } = useFolderContext();
  const { setPage } = usePageContext();
  const { setPageLoad } = usePageLoadContext();
  const { DocAIHistory, setDocAIHistory } = useDocAIContext();
  const [ faiss, setFaiss ] = useState(false);
  const [ faissDisable, setFaissDisable ] = useState(true);
  const [ loading, setLoading ] = useState(false);

  useEffect(() => {
    if (libraries.length > 0) {
      if (libraries[0].toUpperCase().startsWith('CONJUN_') || libraries[0].toUpperCase() === 'CONJECO' || libraries[0].toUpperCase() === 'SAUDEDEBATE') {
        setFaissDisable(false);
        setFaiss(true);
      }
    }

    setItems(DocAIHistory ?? []);

    const buffer = document.getElementById("docaidiv");
    setTimeout(() => {
      if (buffer) buffer.scrollTop = buffer.scrollHeight;
    }, 100);
  }, [DocAIHistory]);

  async function sendAI() {
    if (prompt.trim() === '') return;

    const buffer = document.getElementById("docaidiv");

    setLoading(true);

    const docaiprops = { library: libraries, text: prompt, by_sent: false } as DocAIModel;

    const timeStart = performance.now();

    var newItems = [...items, {id: items.length, question: docaiprops?.text, answer: "...", reasoningopen: false, date: new Date()} as DocAIResultModel];
    setItems(newItems);

    setTimeout(() => {
      if (buffer) buffer.scrollTop = buffer.scrollHeight;
    }, 100);

    try {
      const searchTmp = await docAIApi(docaiprops, tokenContext?.token ?? '', faiss);

      setRefresh(false);

      newItems[newItems.length - 1] = {...newItems[newItems.length -1], 
                                        answer: extractAnswer(searchTmp?.answer ?? ''), 
                                        reasoning: extractReasoning(searchTmp?.answer ?? ''), 
                                        citations: searchTmp?.citations ?? [],
                                        keywords: searchTmp?.keywords ?? []
                                      };

      newItems.map((item) => item.citations = filterUniqueDevices(item.citations));
      setPrompt('');
    } 
    catch (error: any) {
      newItems[newItems.length - 1] = {...newItems[newItems.length -1], 
                                        answer: error?.message ?? '*** Erro ***',
                                        reasoning: '', 
                                        citations: [],
                                        keywords: []
                                      };
    }

    const timeEnd = performance.now();
    var timeDiff = timeEnd - timeStart; 
    newItems[newItems.length - 1] = {...newItems[newItems.length -1], 
                                        elapsedtime: timeDiff / 1000
                                    }

    setTimeout(() => {
      if (buffer) buffer.scrollTop = buffer.scrollHeight;
    }, 100);

    setDocAIHistory(newItems);

    setTimeout(() => {
      setItems(newItems);
      setRefresh(true);
      setLoading(false);
    }, 500);
  };

  function filterUniqueDevices(citations: DocAICitationsModel[]): DocAICitationsModel[] {
      const uniqueDevices = new Set<string>(); // Use Set to keep track of unique device ids
      const filteredRows: DocAICitationsModel[] = [];

      for (const citation of citations) {
          if (!uniqueDevices.has(citation.pageID.toString())) {
              //If the device id is not present, add it to the Set and add it to the resulting array
              uniqueDevices.add(citation.pageID.toString());
              filteredRows.push(citation);
          }
      }

      return filteredRows;
  }

  const extractReasoning = (text: string) => {
    const m = text.split('<\/think>');
    return m ? m[0]?.trim().replace('<think>','') : '';
  }

  const extractAnswer = (text: string) => {
    const m = text.split('<\/think>');
    return m ? m[1]?.trim() : '';
  }

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      sendAI();
    }
  }  

  const togglereasoning = (id: number) => {
    var newItems = [...items];
    newItems[id] = {...newItems[id], reasoningopen: !newItems[id].reasoningopen};

    newItems[id].reasoningopen ? setReasoningText(String.fromCodePoint(0x0001F845) + ' Raciocínio') : setReasoningText(String.fromCodePoint(0x0001F847) + ' Raciocínio')
    setItems(newItems);
    setRefresh(true);
  }

  async function showCitation(library: string, pageid: number) {
    const result = await findFolderAndPageByPageId(library, pageid, tokenContext?.token ?? '');

    if (!result) return;

    if (libraries.length === 1) {
      const folder = findFolderById(Folders, result?.folderID);  
      setFolder(folder);
      setPage(result.page);
      setTimeout(() => {
        setPageLoad(true);
      }, 500);
      setIsVisible(false);
    }
    else {
      window.open(`${window.location.origin}/${library}/pageid/${pageid}`, '_blank');
    }

  };

  return (
    <>
      <Window title={showMessage("docai")} onClose={() =>setIsVisible(false)} initialHeight={isMobile ? 0 : 600} initialWidth={isMobile ? 0 : 800} 
              resizable={!isMobile} stage={isMobile ? "FULLSCREEN" : ""} minimizeButton={()=> null}
              maximizeButton={()=> null} restoreButton={()=> null} modal={false} >
        <div id="docaidiv" className="docaidiv">
          <div className="docairesponses" >
            {items.map((item) => (
              <>
                <div>
                  <div key={"q" + item.id} className="docaiquestion">
                    <div style={{fontSize: 'x-small', color: 'gray',  textAlign: 'right'}}>{format(item.date, "dd/MM/yyyy HH:mm:ss")}</div>
                    {item.question}
                  </div>
                </div>

                <div>
                  <div key={"a" + item.id} className="docaianswer">
                    <>
                      {(refresh && item.answer === "...") ?  
                        <div className='docaithinking' />
                      :
                        <>
                          <ReactMarkdown>{item.answer}</ReactMarkdown>
                          {/* <TypewriterEffect text={item.answer} typingSpeed={10} /> */}
                          <div className='right'>
                            <div className="image-container">
                              <img onClick={() => navigator.clipboard.writeText(item.answer)} alt='Copy' width={24} height={24} src={require('../assets/CopyIcon.svg').default} />
                              <div className="tooltip">Copiar</div>
                            </div>
                          </div>
                          <br/>
                          <br/>
                          <b>Citações:</b>
                          {item.citations?.map((citation) => (
                            <span className='docaipagelink' onClick={() => showCitation(citation?.library ?? libraries[0], citation?.pageID ?? 0)}>
                              <br/>
                              {(libraries.length > 1) ? 
                                <>
                                  {citation?.library} -&nbsp; 
                                </> 
                              : 
                              <></>
                              } 
                              Página {citation?.pageID}
                            </span>
                          ))}
                          <br/>
                          <div style={{width: '100%', textAlign: 'right'}}>
                            <span className='docaishowreasoning' onClick={() => togglereasoning(item.id)}><br/>{reasoningtext}</span>
                            {(refresh && item.reasoningopen) ?
                              <>
                                <br/>
                                <br/>
                                <div style={{fontSize: 'small', color: 'gray'}}>Keywords: {item.keywords.join(', ')}. Resposta em: {Math.round(item.elapsedtime)} segundos</div>
                                <div style={{width: '100%', textAlign: 'left'}}>
                                  <ReactMarkdown>{item.reasoning}</ReactMarkdown>
                                </div>
                              </>
                              : 
                              <></>
                            }
                          </div>
                        </>
                      }
                    </>
                  </div>              
                </div>
              </>
          ))}
        </div>
      </div>

      <WindowActionsBar layout='stretched'>
        {/* <div className='docaitextboxdiv'> */}
          <TextBox className='docaitextbox' value={prompt} placeholder={showMessage("prompt")} disabled={(loading)} onChange={(ev: any) => setPrompt(ev.target.value)} onKeyDown={handleKeyDown}
            suffix={() => (
            <>
              <InputSeparator />
              <InputSuffix >
                <Checkbox disabled={faissDisable} label={"faiss"} checked={faiss} onChange={(e) => setFaiss(e.value) }/>

                <Button fillMode={"flat"} rounded={null} onClick={sendAI} disabled={(loading)} >
                  {showMessage("send")}
                </Button>
              </InputSuffix>
            </>
            )}
          />
        {/* </div> */}
      </WindowActionsBar>
    </Window>
    </>
  )
}

export default DocAI;